import React from 'react';
import { Action, withInject } from '@piwikpro/platform';
import {
  Stack,
  Icon,
  Tooltip,
  Text,
  withTranslation,
} from '@piwikpro/ui-components';
import { TrackerSettings, TrackerSettingsResponse } from '../../../TrackerSettings';

export interface CreateNewVisitWhenCampaignChangesProps {
  appId: string
  trackerSettings: TrackerSettings
  globalTrackerSettings: TrackerSettingsResponse
  appTrackerSettings: TrackerSettingsResponse
  t: (key: string) => string
}

export interface CreateNewVisitWhenCampaignChangesState {
  useGlobals: boolean
}

class CreateNewVisitWhenCampaignChanges extends React.Component<
CreateNewVisitWhenCampaignChangesProps,
CreateNewVisitWhenCampaignChangesState
> {
  static readonly fieldName = 'create_new_visit_when_campaign_changes';

  constructor(props) {
    super(props);
    const { appTrackerSettings } = props;

    this.state = {
      useGlobals: appTrackerSettings
        .attributes?.[CreateNewVisitWhenCampaignChanges.fieldName] === undefined,
    };

    this.handleSubmitSwitcher = this.handleSubmitSwitcher.bind(this);
    this.handleSubmitCheckbox = this.handleSubmitCheckbox.bind(this);
  }

  async handleSubmitSwitcher(values, { setFieldValue, setSubmitting }) {
    const { trackerSettings, appId } = this.props;

    try {
      await trackerSettings.updateSettingsForApp(appId, values);
    } catch (err) {
      setFieldValue(
        CreateNewVisitWhenCampaignChanges.fieldName,
        !values[CreateNewVisitWhenCampaignChanges.fieldName],
        false,
      );
    }

    setSubmitting(false);
  }

  async handleSubmitCheckbox(values, { setFieldValue, setSubmitting }) {
    const {
      trackerSettings,
      globalTrackerSettings,
      appId,
    } = this.props;

    try {
      if (values.create_new_visit_when_campaign_changes_global) {
        await trackerSettings.deleteSettingsForApp(
          appId,
          CreateNewVisitWhenCampaignChanges.fieldName,
        );
      } else {
        await trackerSettings.updateSettingsForApp(appId, {
          [CreateNewVisitWhenCampaignChanges.fieldName]:
            globalTrackerSettings.attributes[CreateNewVisitWhenCampaignChanges.fieldName],
        });

        setFieldValue(
          CreateNewVisitWhenCampaignChanges.fieldName,
          globalTrackerSettings.attributes[CreateNewVisitWhenCampaignChanges.fieldName],
          false,
        );
      }
    } catch (err) {
      setFieldValue(
        CreateNewVisitWhenCampaignChanges.fieldName,
        !values.create_new_visit_when_campaign_changes_global,
        false,
      );
      this.setState({
        useGlobals: !values.create_new_visit_when_campaign_changes_global,
      });

      setSubmitting(false);

      return;
    }

    this.setState({
      useGlobals: values.create_new_visit_when_campaign_changes_global,
    });

    setSubmitting(false);
  }

  render() {
    const {
      appTrackerSettings,
      globalTrackerSettings,
      t,
    } = this.props;
    const { useGlobals } = this.state;

    return (
      <Stack vertical>
        <Stack.Item fill>
          <Stack wrap verticalAlignment="center">
            <Stack.Item fill>
              <Stack vertical spacing="narrow">
                <Stack.Item>
                  <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">{t('apps.details.settings.tracking.create-new-visit-when-campaign-changes.title')}</Text>
                </Stack.Item>
                <Stack.Item>
                  <Text as="p">{t('apps.details.settings.tracking.create-new-visit-when-campaign-changes.description')}</Text>
                </Stack.Item>
              </Stack>
            </Stack.Item>
            <Stack.Item>
              <Action.AutoSavedSwitcherControl
                id="app-settings-tracking-create-new-visit-when-campaign-changes"
                initialValue={
                  useGlobals
                    ? globalTrackerSettings.attributes[
                      CreateNewVisitWhenCampaignChanges.fieldName
                    ]
                    : appTrackerSettings.attributes[CreateNewVisitWhenCampaignChanges.fieldName]
                }
                label={t('apps.details.settings.tracking.create-new-visit-when-campaign-changes.title')}
                info={t('apps.details.settings.tracking.create-new-visit-when-campaign-changes.description')}
                name={CreateNewVisitWhenCampaignChanges.fieldName}
                onSubmit={this.handleSubmitSwitcher}
                disabled={useGlobals}
                disabledActionTooltipText={t('app.details.settings.general.use-global-settings.block-reason')}
              />
            </Stack.Item>
          </Stack>
        </Stack.Item>
        <Stack.Item>
          <Stack spacing="narrow">
            <Stack.Item>
              <Action.AutoSavedCheckboxControl
                id="app-settings-create-new-visit-when-campaign-changes-use-globals"
                onSubmit={this.handleSubmitCheckbox}
                initialValue={useGlobals}
                label={t('app.details.settings.general.use-global-settings.label')}
                name="create_new_visit_when_campaign_changes_global"
              />
            </Stack.Item>
            <Stack.Item>
              <Tooltip content={t('app.details.settings.general.use-global-settings.tooltip') as any}>
                <div>
                  <Icon name="info-circle" />
                </div>
              </Tooltip>
            </Stack.Item>
          </Stack>
        </Stack.Item>
      </Stack>
    );
  }
}

export default withInject({
  trackerSettings: 'TrackerSettingsCrate.tracker',
})(
  withTranslation(['administration'])(CreateNewVisitWhenCampaignChanges as any),
);
